import React, { useState, useEffect, useCallback } from 'react';

const DESIGNER_PRIMARY = '#ADC71B';
const DESIGNER_PRIMARY_HOVER = '#9aac3d';
const DESIGNER_PRIMARY_TEXT = '#000000';
const DESIGNER_BORDER = '#d9d9d9';
const CHECKLIST_PHOTO_BUTTON_BG = '#e3eabe';
const toImageUrlArray = (value) => {
  if (!value) return [];
  return Array.isArray(value) ? value.filter(Boolean) : [value];
};

const toImageAnnotationsArray = (value) => {
  if (!Array.isArray(value)) return [];
  return value.map((entry) => (Array.isArray(entry) ? entry : []));
};

const getStableIdFromUrl = (url) => {
  if (!url || typeof url !== 'string') {
    return `img-${Date.now()}`;
  }
  if (url.startsWith('blob:')) {
    const match = url.match(/blob:[^/]+\/([^/]+)$/);
    if (match && match[1]) {
      return `img-${match[1]}`;
    }
  }
  return `img-${
    url
      .split('/')
      .pop()
      ?.replace(/[^a-zA-Z0-9]/g, '') || Date.now()
  }`;
};

/**
 * Text annotation popup used in Annotations.js for creating/editing text and shape annotations.
 * Renders a simple form; position is handled by the parent (Html wrapper).
 */
const TextAnnotationPopup = ({
  position,
  onSubmit,
  onCancel,
  userName = 'User',
  initialData = null,
  readOnly = false,
  isFromTask = false,
  onTaskComplete = null,
  /** When true (checklist shape toolbar), do not show Task checkbox; saved row is never a task from this flow. */
  hideTaskCheckbox = false,
  uploadAnnotationFile = null,
  onOpenImageAnnotation = null,
  onOpenCameraCapture = null
}) => {
  const [text, setText] = useState(initialData?.text ?? '');
  const [message, setMessage] = useState(initialData?.message ?? '');
  const [isHidden, setIsHidden] = useState(initialData?.isHidden ?? false);
  const [isTask, setIsTask] = useState(initialData?.isTask ?? false);
  const [taskStatus, setTaskStatus] = useState(
    initialData?.taskStatus ?? 'pending'
  );
  const [assignedTo, setAssignedTo] = useState(
    initialData?.assignedTo ?? 'designer'
  );
  const [date, setDate] = useState(
    initialData?.date ?? new Date().toLocaleDateString()
  );
  const [imageUrls, setImageUrls] = useState(() =>
    toImageUrlArray(initialData?.imageUrl)
  );
  const [imageAnnotations, setImageAnnotations] = useState(() =>
    toImageAnnotationsArray(initialData?.imageAnnotations)
  );

  useEffect(() => {
    if (initialData) {
      setText(initialData.text ?? '');
      setMessage(initialData.message ?? '');
      setIsHidden(initialData.isHidden ?? false);
      setIsTask(initialData.isTask ?? false);
      setTaskStatus(initialData.taskStatus ?? 'pending');
      setAssignedTo(initialData.assignedTo ?? 'designer');
      setDate(initialData.date ?? new Date().toLocaleDateString());
      setImageUrls(toImageUrlArray(initialData.imageUrl));
      setImageAnnotations(
        toImageAnnotationsArray(initialData.imageAnnotations)
      );
    }
  }, [initialData]);

  const handleSubmit = useCallback(() => {
    const effectiveIsTask = hideTaskCheckbox ? false : isTask;
    onSubmit({
      text,
      message,
      isHidden,
      isTask: effectiveIsTask,
      taskStatus: effectiveIsTask ? taskStatus : undefined,
      assignedTo: effectiveIsTask ? assignedTo : undefined,
      date,
      imageUrl:
        imageUrls.length > 1
          ? imageUrls
          : imageUrls.length === 1
            ? imageUrls[0]
            : undefined,
      imageAnnotations: imageAnnotations?.length ? imageAnnotations : undefined
    });
    if (isFromTask && onTaskComplete) {
      onTaskComplete();
    }
  }, [
    onSubmit,
    text,
    message,
    isHidden,
    isTask,
    hideTaskCheckbox,
    taskStatus,
    assignedTo,
    date,
    imageUrls,
    imageAnnotations,
    isFromTask,
    onTaskComplete
  ]);

  const handleCancel = useCallback(() => {
    onCancel();
  }, [onCancel]);

  const openImageAnnotator = useCallback(
    (nextImageUrl, targetPhotoIndex) => {
      if (!onOpenImageAnnotation || !nextImageUrl) return;
      const imageId = getStableIdFromUrl(nextImageUrl);
      onOpenImageAnnotation({
        imageUrl: nextImageUrl,
        annotations:
          targetPhotoIndex >= 0 &&
          Array.isArray(imageAnnotations[targetPhotoIndex])
            ? imageAnnotations[targetPhotoIndex]
            : [],
        imageId,
        onSave: (savedImageUrl, savedAnnotations) => {
          if (!savedImageUrl) return;
          setImageUrls((prev) => {
            const next = [...prev];
            if (targetPhotoIndex >= 0 && targetPhotoIndex < next.length) {
              next[targetPhotoIndex] = savedImageUrl;
            }
            return next;
          });
          setImageAnnotations((prev) => {
            const next = [...prev];
            if (targetPhotoIndex >= 0) {
              while (next.length <= targetPhotoIndex) {
                next.push([]);
              }
              next[targetPhotoIndex] = Array.isArray(savedAnnotations)
                ? savedAnnotations
                : [];
            }
            return next;
          });
        },
        currentPopupState: {
          ...(initialData || {}),
          text,
          message,
          isHidden,
          isTask,
          taskStatus,
          assignedTo,
          date,
          imageUrl:
            imageUrls.length > 1
              ? imageUrls
              : imageUrls.length === 1
                ? imageUrls[0]
                : nextImageUrl,
          imageAnnotations: imageAnnotations || []
        }
      });
    },
    [
      onOpenImageAnnotation,
      initialData,
      imageUrls,
      imageAnnotations,
      text,
      message,
      isHidden,
      isTask,
      taskStatus,
      assignedTo,
      date
    ]
  );

  const handlePhotoFileSelect = useCallback(
    async (file) => {
      if (!file) return;
      try {
        const uploadedUrl = uploadAnnotationFile
          ? await uploadAnnotationFile(file, 'photo')
          : URL.createObjectURL(file);
        setImageUrls((prev) => [...prev, uploadedUrl]);
        setImageAnnotations((prev) => [...prev, []]);
      } catch (error) {
        console.error('Failed to upload annotation image:', error);
      }
    },
    [uploadAnnotationFile]
  );

  const openImagePicker = useCallback(
    (withCameraCapture = false) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      if (withCameraCapture) {
        input.setAttribute('capture', 'environment');
      }
      input.onchange = async (e) => {
        const files = e.target?.files;
        const selectedFile = files && files[0] ? files[0] : null;
        await handlePhotoFileSelect(selectedFile);
      };
      input.click();
    },
    [handlePhotoFileSelect]
  );

  const handleTakePhoto = useCallback(() => {
    if (onOpenCameraCapture) {
      onOpenCameraCapture({
        onCapture: async (file) => {
          await handlePhotoFileSelect(file);
        },
        currentPopupState: {
          ...(initialData || {}),
          text,
          message,
          isHidden,
          isTask,
          taskStatus,
          assignedTo,
          date,
          imageUrl:
            imageUrls.length > 1
              ? imageUrls
              : imageUrls.length === 1
                ? imageUrls[0]
                : '',
          imageAnnotations
        }
      });
      return;
    }
    openImagePicker(true);
  }, [
    onOpenCameraCapture,
    handlePhotoFileSelect,
    initialData,
    text,
    message,
    isHidden,
    isTask,
    taskStatus,
    assignedTo,
    date,
    imageUrls,
    imageAnnotations,
    openImagePicker
  ]);

  if (!position) return null;

  return (
    <div
      className="text-annotation-popup"
      style={{
        padding: '12px',
        minWidth: '300px',
        maxWidth: '400px',
        background: '#fff',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        fontSize: '14px'
      }}
    >
      <div style={{ marginBottom: '8px' }}>
        <label
          style={{ display: 'block', marginBottom: '4px', fontWeight: 600 }}
        >
          Title
        </label>
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Annotation title"
          readOnly={readOnly}
          style={{
            width: '100%',
            padding: '6px 8px',
            border: '1px solid #ccc',
            borderRadius: '4px',
            boxSizing: 'border-box'
          }}
        />
      </div>
      <div style={{ marginBottom: '8px' }}>
        <label
          style={{ display: 'block', marginBottom: '4px', fontWeight: 600 }}
        >
          Message
        </label>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Notes"
          readOnly={readOnly}
          rows={3}
          style={{
            width: '100%',
            padding: '6px 8px',
            border: '1px solid #ccc',
            borderRadius: '4px',
            boxSizing: 'border-box',
            resize: 'vertical'
          }}
        />
      </div>
      {!readOnly && (
        <>
          {!hideTaskCheckbox && (
            <div style={{ marginBottom: '8px' }}>
              <label
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                <input
                  type="checkbox"
                  checked={isTask}
                  onChange={(e) => setIsTask(e.target.checked)}
                  style={{ accentColor: DESIGNER_PRIMARY }}
                />
                Task
              </label>
            </div>
          )}
          <div style={{ marginBottom: '8px' }}>
            <label
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <input
                type="checkbox"
                checked={isHidden}
                onChange={(e) => setIsHidden(e.target.checked)}
                style={{ accentColor: DESIGNER_PRIMARY }}
              />
              Hidden
            </label>
          </div>
          {/* Take Photo / Upload Photo — temporarily hidden (designer bugfix); handlers remain above. */}
          {/*
          <div
            style={{
              marginBottom: '8px',
              display: 'flex',
              gap: '8px',
              width: '100%'
            }}
          >
            <button
              type="button"
              onClick={handleTakePhoto}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = DESIGNER_PRIMARY;
                e.currentTarget.style.borderColor = DESIGNER_PRIMARY;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = CHECKLIST_PHOTO_BUTTON_BG;
                e.currentTarget.style.borderColor = CHECKLIST_PHOTO_BUTTON_BG;
              }}
              style={{
                flex: 1,
                padding: '6px 12px',
                border: `1px solid ${CHECKLIST_PHOTO_BUTTON_BG}`,
                borderRadius: '4px',
                background: CHECKLIST_PHOTO_BUTTON_BG,
                color: '#000000',
                cursor: 'pointer'
              }}
            >
              Take Photo
            </button>
            <button
              type="button"
              onClick={() => openImagePicker(false)}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = DESIGNER_PRIMARY;
                e.currentTarget.style.borderColor = DESIGNER_PRIMARY;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = CHECKLIST_PHOTO_BUTTON_BG;
                e.currentTarget.style.borderColor = CHECKLIST_PHOTO_BUTTON_BG;
              }}
              style={{
                flex: 1,
                padding: '6px 12px',
                border: `1px solid ${CHECKLIST_PHOTO_BUTTON_BG}`,
                borderRadius: '4px',
                background: CHECKLIST_PHOTO_BUTTON_BG,
                color: '#000000',
                cursor: 'pointer'
              }}
            >
              Upload Photo
            </button>
          </div>
          */}
        </>
      )}
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '8px',
          marginTop: '12px'
        }}
      >
        <button
          type="button"
          onClick={handleCancel}
          style={{
            padding: '6px 12px',
            border: `1px solid ${DESIGNER_BORDER}`,
            borderRadius: '4px',
            background: '#ffffff',
            color: '#262626',
            cursor: 'pointer'
          }}
        >
          Cancel
        </button>
        {!readOnly && (
          <button
            type="button"
            onClick={handleSubmit}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = DESIGNER_PRIMARY_HOVER;
              e.currentTarget.style.borderColor = DESIGNER_PRIMARY_HOVER;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = DESIGNER_PRIMARY;
              e.currentTarget.style.borderColor = DESIGNER_PRIMARY_HOVER;
            }}
            style={{
              padding: '6px 12px',
              border: `1px solid ${DESIGNER_PRIMARY_HOVER}`,
              borderRadius: '4px',
              background: DESIGNER_PRIMARY,
              color: DESIGNER_PRIMARY_TEXT,
              cursor: 'pointer'
            }}
          >
            Save
          </button>
        )}
        {readOnly && (
          <button
            type="button"
            onClick={handleCancel}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = DESIGNER_PRIMARY_HOVER;
              e.currentTarget.style.borderColor = DESIGNER_PRIMARY_HOVER;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = DESIGNER_PRIMARY;
              e.currentTarget.style.borderColor = DESIGNER_PRIMARY_HOVER;
            }}
            style={{
              padding: '6px 12px',
              border: `1px solid ${DESIGNER_PRIMARY_HOVER}`,
              borderRadius: '4px',
              background: DESIGNER_PRIMARY,
              color: DESIGNER_PRIMARY_TEXT,
              cursor: 'pointer'
            }}
          >
            Close
          </button>
        )}
      </div>
    </div>
  );
};

export { TextAnnotationPopup };
